/**
 * LeaseAmendmentsSection
 * Post-signing amendment entry point and status (US-38/41/42/44, BR-LA-07/11).
 * Rendered for EVERY lease on the Documents tab — wizard-generated and manually-registered
 * (BYO) alike. A BYO lease has no LeaseDocument, so it is add-only (BR-LA-07), but it must
 * still have a way in. Used in LeaseDetailPage.
 */

import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { readSigningResult, signingCeremonyPath, type SigningLaunchState } from '../../utils/signing-navigation';
import {
  listAmendments,
  getEffectiveAddendums,
  voidAmendment,
  downloadEnvelopeDocument,
  getSigningEnvelope,
  resendAmendmentInvitation,
  type LeaseAmendmentResponse,
  type LeaseAmendmentStatus,
  type EffectiveAddendum,
} from '../../lib/api';
import type { EnvelopeDetailResponse } from '@resida/api-types/lease-support';
import { formatTimestamp, deriveTimezone } from '../../utils/date';
import { asIsoDateTimeString } from '@resida/shared';
import { useUnitTimezone } from '../../hooks/useUnitTimezone';
import { ConfirmDialog } from '../ConfirmDialog';
import { PendingInvitations } from './LeaseDocumentSection';

// Amendment statuses that count as "one already in flight" — BR-LA-11 gates a second
// amendment from starting while one of these is in progress for the lease.
const IN_FLIGHT_AMENDMENT_STATUSES: LeaseAmendmentResponse['status'][] = [
  'DRAFT',
  'GENERATED',
  'SENT_FOR_SIGNING',
];

const IN_FLIGHT_AMENDMENT_STATUS_LABEL: Record<LeaseAmendmentStatus, string> = {
  DRAFT: 'Draft — changes staged, not yet generated',
  GENERATED: 'Generated — ready to send for signature',
  SENT_FOR_SIGNING: 'Awaiting signatures',
  SIGNED: 'Signed',
  VOIDED: 'Voided',
};

interface LeaseAmendmentsSectionProps {
  leaseId: string;
  unitId: string;
}

export function LeaseAmendmentsSection({ leaseId, unitId }: LeaseAmendmentsSectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [amendments, setAmendments] = useState<LeaseAmendmentResponse[]>([]);
  const [effectiveAddendums, setEffectiveAddendums] = useState<EffectiveAddendum[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  // BR-LA-20: whether the landlord's own recipient on the in-flight amendment's envelope is still
  // unsigned (drives "Sign" vs "View Envelope"). Null until known / on failure (non-fatal).
  const [landlordSignPending, setLandlordSignPending] = useState<boolean | null>(null);
  const [downloadingAmendmentId, setDownloadingAmendmentId] = useState<string | null>(null);
  const [isVoidingAmendment, setIsVoidingAmendment] = useState(false);
  const timezoneQuery = useUnitTimezone(unitId);
  const timezone = timezoneQuery.data ?? deriveTimezone('');

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    onConfirm: () => void;
  }>({ isOpen: false, onConfirm: () => {} });

  // Returning from the full-screen signing route (BR-LA-20): confirm the landlord's signature
  // once, then drop the one-shot router state so a reload does not replay it.
  useEffect(() => {
    if (readSigningResult(location.state) === 'completed') {
      setActionMessage('Your signature was recorded. The amendment takes effect once every signer has signed.');
    }
    if (readSigningResult(location.state)) {
      navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAmendments();
    loadEffectiveAddendums();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId]);

  // BR-LA-20: when the in-flight amendment is out for signing, look up the landlord's own
  // recipient so the card can offer "Sign" (unsigned) or "View Envelope" (already signed).
  // A failed lookup is non-fatal — the card falls back to the plain "View Envelope" link.
  const sentEnvelopeId =
    amendments.find(a => a.status === 'SENT_FOR_SIGNING')?.signingEnvelopeId ?? null;
  useEffect(() => {
    if (!sentEnvelopeId) {
      setLandlordSignPending(null);
      return;
    }
    let cancelled = false;
    getSigningEnvelope(sentEnvelopeId)
      .then(envelope => {
        if (cancelled) return;
        const recipients = (envelope as EnvelopeDetailResponse).recipients ?? [];
        const mine = recipients.find(r => r.recipientType === 'manager');
        setLandlordSignPending(mine ? mine.status === 'SENT' || mine.status === 'OPENED' : null);
      })
      .catch(() => {
        if (!cancelled) setLandlordSignPending(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sentEnvelopeId]);

  async function loadAmendments() {
    try {
      setAmendments(await listAmendments(leaseId));
    } catch {
      // Non-fatal — the executed-amendments list stays empty rather than blocking the page.
      setAmendments([]);
    }
  }

  async function loadEffectiveAddendums() {
    try {
      const result = await getEffectiveAddendums(leaseId);
      // DC-23 returns { effective, recordedVariables }; titles (incl. custom addendums) come from the server.
      setEffectiveAddendums(result.effective);
    } catch {
      // Non-fatal — the "Currently in Effect" list stays empty (US-42).
      setEffectiveAddendums([]);
    }
  }

  function handleVoidAmendment(amendment: LeaseAmendmentResponse) {
    setConfirmDialog({
      isOpen: true,
      onConfirm: async () => {
        try {
          setIsVoidingAmendment(true);
          setError(null);
          setActionMessage(null);
          await voidAmendment(leaseId, amendment.id);
          setActionMessage('Amendment voided');
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          await loadAmendments();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to void amendment');
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        } finally {
          setIsVoidingAmendment(false);
        }
      },
    });
  }

  async function handleDownloadAmendment(amendment: LeaseAmendmentResponse) {
    if (!amendment.signingEnvelopeId) return;
    try {
      setDownloadingAmendmentId(amendment.id);
      setError(null);
      const response = await downloadEnvelopeDocument(amendment.signingEnvelopeId);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lease-amendment-${amendment.sequenceNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download amendment document');
    } finally {
      setDownloadingAmendmentId(null);
    }
  }

  const inFlightAmendment = amendments.find(a => IN_FLIGHT_AMENDMENT_STATUSES.includes(a.status)) ?? null;
  const executedAmendments = amendments.filter(a => a.status === 'SIGNED');

  return (
    <div data-testid="lease-amendments-section" className="bg-card rounded-lg p-5 border border-border">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-foreground m-0">Amendments</h2>
        {!inFlightAmendment && (
          <Link
            to={`/leases/${leaseId}/amendments/new`}
            data-testid="add-amendment-button"
            className="px-4 py-2 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-md hover:bg-primary/10 no-underline min-h-[44px] inline-flex items-center"
          >
            Add Amendment
          </Link>
        )}
      </div>

      {error && (
        <div className="p-3 mt-4 bg-destructive/5 border border-destructive/20 rounded-md text-sm text-destructive">
          {error}
        </div>
      )}

      {actionMessage && (
        <div className="p-3 mt-4 bg-success/10 border border-success/20 rounded-md text-sm text-success">
          {actionMessage}
        </div>
      )}

      {/* In-flight amendment status (FM5/FM10) — a landlord who starts an amendment always
          has a way back into it, and a way to abandon it via Void (DC-22), instead of the
          "Add Amendment" button simply disappearing with no trace. */}
      {inFlightAmendment && (
        <div data-testid="in-flight-amendment-card" className="mt-4 pt-4 border-t border-border flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-foreground m-0">Amendment in progress</h3>
              <p className="text-xs text-muted-foreground mt-1 m-0" data-testid="in-flight-amendment-status">
                {IN_FLIGHT_AMENDMENT_STATUS_LABEL[inFlightAmendment.status]}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {inFlightAmendment.status === 'SENT_FOR_SIGNING' && inFlightAmendment.signingEnvelopeId ? (
                landlordSignPending === true ? (
                  <Link
                    to={signingCeremonyPath(inFlightAmendment.signingEnvelopeId)}
                    state={{ returnTo: `/leases/${leaseId}#documents` } satisfies SigningLaunchState}
                    data-testid="in-flight-amendment-sign-link"
                    className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-primary rounded-md hover:bg-primary/90 no-underline min-h-[44px] inline-flex items-center"
                  >
                    Sign
                  </Link>
                ) : (
                  <Link
                    to={`/signing/${inFlightAmendment.signingEnvelopeId}`}
                    data-testid="in-flight-amendment-view-envelope-link"
                    className="px-4 py-2 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-md hover:bg-primary/10 no-underline min-h-[44px] inline-flex items-center"
                  >
                    View Envelope
                  </Link>
                )
              ) : (
                <Link
                  to={`/leases/${leaseId}/amendments/new`}
                  data-testid="in-flight-amendment-resume-link"
                  className="px-4 py-2 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-md hover:bg-primary/10 no-underline min-h-[44px] inline-flex items-center"
                >
                  Resume
                </Link>
              )}
              <button
                type="button"
                data-testid="in-flight-amendment-void-button"
                onClick={() => handleVoidAmendment(inFlightAmendment)}
                disabled={isVoidingAmendment}
                className="px-4 py-2 text-sm font-medium text-destructive bg-card border border-destructive/20 rounded-md hover:bg-destructive/5 disabled:opacity-50 min-h-[44px]"
              >
                {isVoidingAmendment ? 'Voiding...' : 'Void'}
              </button>
            </div>
          </div>
          {inFlightAmendment.status === 'SENT_FOR_SIGNING' && inFlightAmendment.signingEnvelopeId && (
            <PendingInvitations
              envelopeId={inFlightAmendment.signingEnvelopeId}
              onResend={recipientId => resendAmendmentInvitation(leaseId, inFlightAmendment.id, recipientId)}
              onResent={() => void loadAmendments()}
            />
          )}
        </div>
      )}

      {/* Currently-effective addendum set (US-42) — what's actually in force today,
          without a landlord having to mentally replay every amendment's add/modify/delete. */}
      {effectiveAddendums.length > 0 && (
        <div data-testid="effective-addendums-section" className="mt-4 pt-4 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-2">Currently in Effect</h3>
          <div className="flex flex-col gap-1">
            {effectiveAddendums.map(addendum => (
              <div
                key={addendum.slug}
                data-testid={`effective-addendum-${addendum.slug}`}
                className="text-xs text-muted-foreground px-2 py-1 bg-accent/50 rounded"
              >
                {addendum.title}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Executed Amendments (US-41) */}
      {executedAmendments.length > 0 && (
        <div data-testid="executed-amendments-section" className="mt-4 pt-4 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-2">Executed Amendments</h3>
          <div className="flex flex-col gap-1">
            {executedAmendments.map(amendment => (
              <div
                key={amendment.id}
                data-testid={`executed-amendment-${amendment.id}`}
                className="flex justify-between items-center text-xs text-muted-foreground px-2 py-1 bg-accent/50 rounded"
              >
                <span>
                  Amendment #{amendment.sequenceNumber}
                  {amendment.effectiveDate && ` — effective ${formatTimestamp(asIsoDateTimeString(amendment.effectiveDate), timezone, { year: 'numeric', month: 'short', day: 'numeric' })}`}
                </span>
                {amendment.signingEnvelopeId && (
                  <button
                    type="button"
                    data-testid={`executed-amendment-download-${amendment.id}`}
                    onClick={() => handleDownloadAmendment(amendment)}
                    disabled={downloadingAmendmentId === amendment.id}
                    className="text-primary hover:underline disabled:opacity-50 min-h-[44px] px-1"
                  >
                    {downloadingAmendmentId === amendment.id ? 'Downloading...' : 'Download'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title="Void Amendment"
        message="Are you sure you want to void this in-progress amendment? Any staged changes will be discarded and this cannot be undone."
        confirmText="Void Amendment"
        isDestructive
      />
    </div>
  );
}
